/**
 * ai_agent — re-export shim over bunnyquery/engine.
 *
 * The chat request/transport/prompt logic now lives in the framework-agnostic
 * `bunnyquery/engine` package (shared with the BunnyQuery widget). This file
 * keeps the historical `@/code/ai_agent` import path working unchanged for the
 * rest of the app, wires the engine's transport to this app's skapi instance,
 * and keeps the two pieces of Vue-reactive, app-level shared state that are NOT
 * engine-owned (they are read/written across dbfile.vue, agent.vue, main.vue).
 */
import { reactive, ref } from 'vue';
import { skapi } from '@/main';
import { currentService } from '@/views/project-list';
import { MCP_BASE_URL } from './mcp_oauth';
import {
	configureChatEngine,
	ChatSession,
	indexDoneUniqueId,
	runIndexUniqueId,
	// Whether THIS BUILD's skapi-js can carry skapi's half of the stream flag.
	// Shared with the widget rather than written out twice, because it is exactly the
	// kind of predicate that forks and the two clients are diffed. The widget must
	// ask it because init() takes the EMBEDDER's instance; here the instance is the
	// repo's own, so only a stale node_modules or an unbuilt skapi-js can fail it -
	// which is exactly the state a dev flipping LIVE_STREAMING is most likely to be
	// in, and a silently empty chat is the worst possible way to find out.
	skapiSupportsStreaming,
	parseAiAgentValue,
	// The project's BunnyQuery settings record. Taken straight from the engine
	// rather than through '@/code/access_group' (which is only a shim over these)
	// so this file, which is where the store's reader is installed, imports it
	// from the module that owns it.
	configureProjectSettings,
	PROJECT_SETTINGS_UNIQUE_ID,
	DEFAULT_UPLOAD_ACCESS_GROUP,
	normalizeUploadAccessGroup,
	readyProjectSettings,
	projectUploadAccessGroup,
	type ChatHost,
	type ChatIdentity,
	type BgTaskEntry,
	type IndexRunPatch,
	type UploadAccessGroup,
} from 'bunnyquery/engine';

// Live streaming of chat turns, as ONE constant to flip. Off, and for the same
// shipping-order reason windowedIndexing had one: THE RELAYING POLLING WORKER
// MUST SHIP FIRST. With this on against a region whose worker does not relay,
// the request either has its `since` cursor rejected or the row keeps an SSE
// transcript where the readers expect a parsed document, and the turn reads back
// as an empty answer. A streamed row settles with a STATUS AND NO BODY on
// purpose, so there is no fallback to read. Turn it on once the worker is
// deployed in every region this app talks to, which (unlike the widget's
// per-embed init opt) is all of them, because one dashboard build serves every
// project's region.
// It also needs a skapi-js that supports `stream`/`onStream`, which this constant
// cannot promise on its own: see canStream() below, which is what grants it.
//
// Typed `boolean` rather than left to infer the literal `false`, so flipping it is a
// one-token edit: without the annotation TS narrows every value derived from it to
// `false` and the gate below reads as dead code to anyone (and to any future lint)
// looking at it.
const LIVE_STREAMING: boolean = true;

// The constant ASKS; the SDK grants. Degrade to BUFFERED rather than to a broken
// request: without skapi's half of the flag the destination still streams SSE into a
// buffered row and every answer reads back empty, which is worse than not streaming
// in every way. Loud, because there is no other symptom to find it by.
//
// EVALUATED LAZILY, AND THAT IS NOT A STYLE CHOICE. `skapi` is imported from
// '@/main', which imports back into this module, so at the moment this file is
// evaluated that binding is still in its temporal dead zone: touching it here throws
// "Cannot access 'skapi' before initialization" and takes the whole app down at load.
// Every other use of `skapi` in this file is already inside a callback and so is
// deferred to call time; this check has to be too. Memoised because the answer cannot
// change within a session, and warned once for the same reason.
let _canStream: boolean | null = null;

/** Can this build's skapi-js carry skapi's half of the stream flag? Capability only. */
function canStream(): boolean {
	if (_canStream === null) {
		_canStream = skapiSupportsStreaming(skapi);
		if (LIVE_STREAMING && !_canStream) {
			console.warn(
				"[chat-engine] liveStreaming is on but this build's skapi-js has no " +
				'clientSecretRequestStream/clientSecretRequestFinalize, so skapi\'s half of the ' +
				'stream flag would be dropped and every reply would read back empty. ' +
				'Falling back to buffered replies - rebuild/update skapi-js to enable streaming.',
			);
		}
	}
	return _canStream;
}

/**
 * Should NEW requests stream? The flag asks, the capability grants.
 *
 * Deliberately NOT the gate on the two hooks below. Those are the way back to a
 * request that ALREADY streamed, and whether to read one back is a different question
 * from whether to stream the next one: gating them here would mean turning the flag
 * off stranded every row that had already streamed, with its answer unreachable.
 * They are gated on canStream() alone.
 */
function streamingOn(): boolean {
	return LIVE_STREAMING && canStream();
}

/** Turns whose first websocket chunk has already been announced, so the notice is
 *  printed once per turn rather than once per paint. Cleared when the turn ends. */
const socketAnnounced: Record<string, boolean> = {};

// Inject this app's transport + MCP endpoint into the engine. `poll` is omitted:
// the npm-bundled skapi-js used here resolves clientSecretRequest with the final
// body directly (the BunnyQuery widget, on skapi-js@latest, sets poll: 0).
configureChatEngine({
	clientSecretRequest: (opts: any) => (skapi.clientSecretRequest as any)(opts),
	clientSecretRequestHistory: (params: any, fetchOptions: any) =>
		(skapi as any).clientSecretRequestHistory(params, fetchOptions),
	mcpBaseUrl: MCP_BASE_URL,
	windowedIndexing: true,
	mintIndexDoneMarker: ({ service, storagePath }) => { void mintIndexDoneMarker(service, storagePath); },
	upsertIndexRunRecord: ({ service, storagePath, patch }) => { void upsertIndexRunRecord(service, storagePath, patch); },
	// Single-item csr-poll point lookup: how the engine hydrates a compact
	// history stub's real body when the user expands an indexing row.
	csrHistoryItemLookup: (fullId: string, service: string, owner: string) =>
		(skapi as any).util.request('csr-poll', { id: fullId, service, owner }, { auth: true }),
	// ---- live streaming (mirrored in the widget's src/index.js) -------------
	// See LIVE_STREAMING above for why this is off and what turns it on, and
	// skapiSupportsStreaming for why the constant alone does not decide it.
	// A GETTER, not a value: configureChatEngine stores this object by reference and
	// liveStreamingEnabled() reads the property at call time, so the capability check
	// runs on first use rather than while this module is still evaluating.
	get liveStreaming() { return streamingOn(); },
	// Socket delivery, a separate opt-in from streaming itself. Safe to take HERE and
	// not in the widget: joinRealtime replaces the connection's group rather than
	// adding to it, so for the length of a turn this takes the room. This app owns its
	// skapi instance and uses realtime for nothing else (grep: no other
	// connectRealtime/joinRealtime caller), so there is no room to take from anyone.
	// The widget is handed the EMBEDDER'S instance and cannot know that, so it leaves
	// this off unless the embedder asks.
	get liveStreamingRealtime() { return streamingOn(); },
	// What stores the version of a streamed turn that history keeps. The engine
	// sends the ASSEMBLED provider body, so a streamed turn reads back through
	// exactly the extractors a buffered one does, with no branch in the mapper;
	// storing is also what releases the chunks. Called only for a streamed turn,
	// and best-effort inside the engine. Handed over only when the SDK actually
	// has it, so the engine's own "is this host able to?" checks answer honestly
	// instead of a call reaching an undefined method mid-turn.
	get clientSecretRequestFinalize() {
		return canStream()
			? (requestId: string, data: any, options: any) =>
				(skapi as any).clientSecretRequestFinalize(requestId, data, options)
			: undefined;
	},
	// THE SECOND HALF OF THE DURABILITY GUARANTEE. A streamed row settles with a
	// status and NO body: the answer is chunks until finalize copies a version
	// onto the row. A row that settles while no poll is attached (the tab was
	// closed, a mobile browser discarded it, the device slept and the interval
	// stopped) is therefore never finalized, and without this hook the engine has
	// no way back to it - the answer reads as gone from the conversation with
	// every byte of it still stored. Given the request id this drains that turn's
	// chunks in one pass, and the engine parses them exactly as it parses a live
	// stream, finalizing what it read so the row becomes ordinary history and is
	// never re-read.
	get clientSecretRequestStream() {
		return canStream()
			? (requestId: string, options: any) =>
				(skapi as any).clientSecretRequestStream(requestId, options)
			: undefined;
	},
	// WHICH TRANSPORT ACTUALLY DELIVERED THE ANSWER, in the console.
	//
	// skapi hands socket-relayed text and polled text to the same callback on
	// purpose - that is what makes the socket a pure accelerator and lets a turn
	// survive a dropped connection - so from the outside a perfectly working
	// websocket and a websocket that never connected look identical. This is the
	// only place that difference is visible.
	//
	// Two lines per turn at most: the moment the socket first delivers, and the
	// split when the turn ends. Never one per chunk; a long answer is thousands.
	onLiveStreamUpdate: (u) => {
		if (u.phase === 'end') {
			delete socketAnnounced[u.serverItemId];
			console.log(
				'%c[chat] stream delivered',
				'color: #0a0;',
				`${u.transport.socket} chunk(s) over WEBSOCKET, ${u.transport.poll} over poll`,
				u.serverItemId,
			);
			return;
		}
		if (u.transport.socket > 0 && !socketAnnounced[u.serverItemId]) {
			socketAnnounced[u.serverItemId] = true;
			console.log('%c[chat] websocket is delivering this answer', 'color: #0a0;', u.serverItemId);
		}
	},
});

// Inject the reader the project-settings store fetches with, once, for the same
// reason the transport above is injected: the engine never imports a skapi
// instance. One record per project holds every BunnyQuery project setting, so
// this is one point lookup by unique_id and never a table scan.
//
// DELIBERATELY NOT CAUGHT HERE. The store catches for us and settles a failed
// read as "no record", which every accessor answers with the default: a signed
// out visitor on a require_login project gets REQUIRE_LOGIN from this call, which
// is an ordinary outcome and not something a user should ever be shown. Catching
// it here as well would only hide which failures the store is absorbing.
configureProjectSettings(async (service: string) => {
	const found: any = await (skapi as any).getRecords({ service, unique_id: PROJECT_SETTINGS_UNIQUE_ID });
	return found?.list?.[0]?.data || null;
});

export * from 'bunnyquery/engine';

// Delete a file's AI-index record ("src::<storagePath>") ahead of a re-index or
// overwrite, so the agent re-creates it fresh instead of colliding/duplicating.
// The skapi backend cascades a src:: record delete to every record that
// references it (its reference-linked children), so this single call clears the
// file's whole reference-linked index subtree. Best-effort: a missing record (a
// file that was never indexed) or a permission/transport error must NOT fail the
// upload — the subsequent index request still runs and re-creates the record.
export async function deleteFileIndexRecord(service: string, storagePath: string): Promise<void> {
	if (!service || !storagePath) return;
	const prefix = 'src::' + storagePath;

	// Deleting only the exact "src::<path>" record leaves orphans behind. The agent also
	// creates DECORATED unique_ids of its own for per-window summaries, seen in real runs as
	// "src::<path>::sheet_기체::R54_window" and "src::<path>|file_summary|window190". Those are
	// separate records, NOT reference-linked children of the plain one, so the backend's cascade
	// never touches them and a re-index stacks a fresh set on top of the old. So: find everything
	// whose unique_id STARTS WITH the prefix (getUniqueId with '>=' is a starts-with search on a
	// string value) and delete all of it.
	let ids: string[] = [];
	try {
		const found: any = await (skapi as any).getUniqueId(
			{ unique_id: prefix, condition: '>=' },
			{ fetchMore: false, limit: 1000 }
		);
		ids = (found?.list || [])
			.map((row: any) => String(row?.unique_id || ''))
			.filter(Boolean);

		// A bare prefix match also catches a DIFFERENT FILE: "src::report.xlsx" is a prefix of
		// "src::report.xlsx.bak" and "src::report.xlsx (1).xlsx", both of which can be real files with
		// their own index, and there is no cheap existence probe to ask with.
		//
		// So a continuation only counts as a DECORATION when its first character is one that cannot
		// begin a filename: < > : " / \ | ? *  (the reserved set). The agent's own decorations use
		// them ("src::<path>::sheet_기체::R54_window", "src::<path>|file_summary|window190"), while the
		// real-world derived names that would be false positives continue with '.', a space, '-' or
		// '_', none of which are in the set. That asymmetry is the whole point of choosing it.
		//
		// Caveat: these characters are legal in an S3 key and nothing sanitises uploaded filenames, so
		// this is "very unlikely to collide", not "impossible". A false positive needs a file whose
		// path is ANOTHER file's complete path plus one of these characters plus more, which is
		// contrived. Anything not matching is left alone and logged rather than guessed at.
		const DECORATION_START = /^[<>:"/\\|?*]/;
		const decorations = ids.filter(id => id !== prefix && DECORATION_START.test(id.slice(prefix.length)));
		const ambiguous = ids.filter(id => id !== prefix && !decorations.includes(id));
		if (ambiguous.length) {
			// Visible rather than silent: these MIGHT be this file's leftovers, or another file
			// entirely. Left in place on purpose.
			console.warn('[ai_agent] deleteFileIndexRecord: leaving ' + ambiguous.length
				+ ' ambiguous prefix match(es) in place, cannot prove they belong to '
				+ storagePath + ':', ambiguous);
		}
		ids = decorations;
	} catch (err) {
		console.warn('[ai_agent] deleteFileIndexRecord: unique-id sweep failed (non-fatal)', storagePath, err);
	}

	if (!ids.includes(prefix)) ids.push(prefix);   // always try the plain one, even if unlisted

	// The backend's "indexing finished" marker (done::<path>) references the src::
	// record, so the cascade normally sweeps it - but records created before the
	// cascade flag existed have no cascade, and a re-index must NEVER start with a
	// stale "finished" verdict standing. Explicit delete, same best-effort rules.
	// The run:: status record rides the same cascade and needs the same explicit
	// delete for the same reason: a rerun must never inherit the old run's verdict.
	ids.push(indexDoneUniqueId(storagePath));
	ids.push(runIndexUniqueId(storagePath));

	// Best-effort and INDEPENDENT per id: a missing record (a file that was never indexed) or a
	// permission error must not stop the rest, nor fail the upload. The subsequent index request
	// still runs.
	//
	// Deleting the plain record ALSO cascades to every record referencing it, which is what clears
	// the bulk of the subtree whatever unique_id the agent gave those children. That cascade is not
	// automatic: it happens only because ensureFileIndexRecord sets
	// source.can_remove_referencing_records on the src:: record. Records written BEFORE that flag
	// was added have no cascade and are not reachable by the prefix sweep either, so a file indexed
	// before it still needs a one-off cleanup.
	for (const unique_id of ids) {
		try {
			await (skapi as any).deleteRecords({ service, unique_id });
		} catch (err) {
			console.warn('[ai_agent] deleteFileIndexRecord failed (non-fatal)', unique_id, err);
		}
	}
}

// Create the file's "src::<path>" record BEFORE any indexing pass runs, so that every pass has a
// reference target that is guaranteed to exist.
//
// Previously this record was created opportunistically by whichever indexing pass happened to do
// it, usually the first, while all 28 passes referenced it. Passes are separate model turns with no
// memory of each other (indexing turns are filtered out of chat history), so a pass could not know
// whether it existed, and delete-then-repost above deliberately destroys it at the start of a
// re-index. When it was absent the backend rejected every referencing row with
// NOT_EXISTS: 'Record of "reference.unique_id": "src::<path>" does not exist'. Measured in a real
// run: 20 records sent, 0 saved.
//
// Only the metadata the CLIENT already knows goes in here. Sheet names, column headers and row
// counts require extraction, so the agent enriches this record later; it must not be blocked on
// information nobody has yet.
// Client-side twin of the MCP server's /internal/index-complete: mint the
// durable "indexing finished" marker for a run whose completion THIS client
// knows deterministically (single-pass settle, or a client-driven chain's
// completion token — the engine's mintIndexDoneMarker hook decides when).
// Identical record shape so every reader treats them the same, including the
// reference to the src:: record, which is what makes a re-index's
// delete-then-repost cascade the marker away. Best-effort: the marker already
// existing (a redelivered settle) is completion, not a failure.
export async function mintIndexDoneMarker(service: string, storagePath: string): Promise<void> {
	if (!service || !storagePath) return;
	try {
		await (skapi as any).postRecord(null, {
			service,
			unique_id: indexDoneUniqueId(storagePath),
			table: { name: '__INDEXING__', access_group: 'authorized' },
			reference: 'src::' + storagePath,
			data: { source: storagePath, completed_at: Date.now() },
		});
	} catch (err: any) {
		const msg = String(err?.message || err || '');
		if (msg.indexOf('is already taken') === -1) {
			console.warn('[ai_agent] mintIndexDoneMarker failed (non-fatal)', storagePath, msg);
		}
	}
}

// Create-or-update the per-file indexing RUN record ("run::<path>"). The engine
// fires this at first-pass enqueue (status 'working') and at every ending it
// observes (done/error/cancelled); the worker writes the same record through the
// MCP internal routes for chain endings only it can see. The records API has no
// upsert, so the order is chosen by which case is COMMON per status: a 'working'
// mint usually targets a MISSING record (the reindex cascade swept the old one)
// and creates straight away; a terminal flip usually targets an EXISTING one and
// looks it up first — create-first there guaranteed a visible 400 ("is already
// taken") in the console on every flip and every stale-sweep re-observation,
// which read as a bug. Precedence: a 'working' write never overwrites a terminal
// status unless it is a NEW run (started after the old run finished); a terminal
// re-write of the same status is skipped outright.
export async function upsertIndexRunRecord(
	service: string,
	storagePath: string,
	patch: IndexRunPatch,
): Promise<void> {
	if (!service || !storagePath || !patch || !patch.status) return;
	const uid = runIndexUniqueId(storagePath);
	const TERMINAL: Record<string, boolean> = { done: true, error: true, cancelled: true };
	const patchData = (base: any) => {
		const d: any = Object.assign({}, base || {}, { source: storagePath, status: patch.status });
		if (patch.filename) d.filename = patch.filename;
		if (typeof patch.started === 'number') d.started = patch.started;
		if (typeof patch.finished === 'number') d.finished = patch.finished;
		if (patch.error) d.error = patch.error;
		if (patch.queue) d.queue = patch.queue;
		// The chat that owns the run: a record is keyed by storage path alone,
		// but a chat is per (project, platform) — see IndexRunPatch.platform.
		if (patch.platform) d.platform = patch.platform;
		return d;
	};
	// Absent and unfetchable both read as "no record" (fetching a missing
	// unique_id can throw rather than return an empty list).
	const lookup = async (): Promise<any | null> => {
		try {
			const found: any = await (skapi as any).getRecords({ service, unique_id: uid });
			return found?.list?.[0] || null;
		} catch { return null; }
	};
	// Returns true when the record is settled (updated, or correctly left alone).
	const updateExisting = async (rec: any): Promise<void> => {
		const existing = rec.data || {};
		if (patch.status === 'working' && TERMINAL[String(existing.status)]) {
			// A NEW run may reopen a terminal record: an orphaned verdict from a
			// path's previous life must not freeze the fresh run out. Only a LATE
			// create from the run the record already describes must lose, and its
			// `started` predates the settle that closed it.
			const endedAt = typeof existing.finished === 'number' ? existing.finished
				: typeof existing.started === 'number' ? existing.started : 0;
			if (!(typeof patch.started === 'number' && patch.started > endedAt)) return;
		}
		// Same terminal verdict already stored: nothing to say. Re-writing it on
		// every stale-sweep re-observation was pure write noise.
		if (patch.status !== 'working' && String(existing.status) === patch.status) return;
		await (skapi as any).postRecord(null, {
			service,
			record_id: rec.record_id,
			data: patchData(existing),
		});
	};
	const create = (withRef: boolean) => (skapi as any).postRecord(null, {
		service,
		unique_id: uid,
		table: { name: '__INDEXING__', access_group: 'authorized' },
		// Cascade linkage, same as done::: a re-index's delete of src:: sweeps
		// the old run record away, so a rerun of the same path starts clean.
		...(withRef ? { reference: 'src::' + storagePath } : {}),
		data: patchData(null),
	});
	try {
		if (patch.status !== 'working') {
			const rec = await lookup();
			if (rec?.record_id) { await updateExisting(rec); return; }
		}
		try {
			await create(true);
			return;
		} catch (err: any) {
			const msg = String(err?.message || err || '');
			if (msg.indexOf('is already taken') === -1) {
				// Not the exists-signal — almost always the src:: record not
				// existing yet. Mint it and retry WITH the reference (a
				// reference-less run record cannot be cascade-swept on reindex);
				// only if that still fails does a reference-less create beat
				// having no record at all.
				try {
					// The project's group, not the parameter default. This mints a
					// src:: stub for a file that has none, and the default would
					// put it at 'authorized' on a project that indexes at public or
					// private: the stub would then sit in a different table from
					// everything else extracted out of that file, and never come
					// back with it. There is no existing record to read the real
					// group off (its absence is why we are here), so the project
					// setting is the best answer available. A project set to 'ask'
					// resolves to the same 'authorized' fallback, since a repair
					// pass has nobody to ask.
					await readyProjectSettings(service);
					await ensureFileIndexRecord(service, storagePath, undefined, projectUploadAccessGroup(service));
					await create(true);
					return;
				} catch (errRef: any) {
					const msgRef = String(errRef?.message || errRef || '');
					if (msgRef.indexOf('is already taken') === -1) {
						try {
							await create(false);
							return;
						} catch (err2: any) {
							const msg2 = String(err2?.message || err2 || '');
							if (msg2.indexOf('is already taken') === -1) {
								console.warn('[ai_agent] upsertIndexRunRecord create failed (non-fatal)', storagePath, msg2);
								return;
							}
						}
					}
				}
			}
		}
		// Taken after all (created behind our back, or a 'working' mint over a
		// surviving record): settle it as an update.
		const rec2 = await lookup();
		if (rec2?.record_id) await updateExisting(rec2);
	} catch (err: any) {
		console.warn('[ai_agent] upsertIndexRunRecord failed (non-fatal)', storagePath, String(err?.message || err || ''));
	}
}

/**
 * The access group a file's existing `src::` record is already at, or null when
 * there is no such record (or it cannot be read).
 *
 * A RE-INDEX must not change a file's visibility. It deletes the `src::` record
 * and posts it again, so without reading the old group first the new record is
 * written at whatever the project default happens to be now: a "Only me" file
 * silently becomes readable by every signed-in user, and on a project set to
 * 'ask' every file in a bulk reindex is rewritten to the fallback at once. Read
 * BEFORE the delete; afterwards there is nothing left to read.
 */
export async function readFileIndexAccessGroup(
	service: string,
	storagePath: string,
): Promise<UploadAccessGroup | null> {
	if (!service || !storagePath) return null;
	try {
		const found: any = await (skapi as any).getRecords({
			service,
			unique_id: 'src::' + storagePath,
		});
		const rec = found?.list?.[0];
		const group = rec?.table?.access_group;
		// Only the three the dashboard can express. A record sitting at a raw
		// group number or 'admin' is left to the caller's fallback rather than
		// being coerced into a neighbouring group.
		if (group === 'private') return 'private';
		if (group === 0 || group === 'public') return 'public';
		if (group === 1 || group === 'authorized') return 'authorized';
		return null;
	} catch (err: any) {
		// "NO SUCH RECORD" ARRIVES AS AN ERROR HERE, AND IT IS AN ANSWER, NOT A FAILURE.
		//
		// A unique_id lookup that matches nothing does not come back as an empty list:
		// the backend raises NOT_EXISTS and the SDK surfaces it as a rejected request
		// with a 400. For this function that is the ORDINARY case, the file has never
		// been indexed, and it must return null so the caller applies the group the
		// upload chose.
		//
		// EVERYTHING ELSE IS UNKNOWN, and unknown must not be reported as "no stored
		// group". The caller reads this to decide whether an EXISTING file keeps the
		// access group it already had, and then DELETES the src:: record that holds it.
		// So a throttle, a network blip or an auth refresh answered as null silently
		// re-classifies a private file to whatever the current upload chose and
		// destroys the only evidence of what it was. With one batch answer covering a
		// whole drop, one flaky moment can do that to several files at once. Those
		// rethrow, and the caller's retry ladder treats them as a failed attempt.
		const code = err?.code || err?.error?.code;
		const message = String(err?.message || '');
		if (code === 'NOT_EXISTS' || /unique id does not exist/i.test(message)) return null;
		throw err;
	}
}

export async function ensureFileIndexRecord(
	service: string,
	storagePath: string,
	meta?: { name?: string; mime?: string; size?: number },
	// Who may read what is extracted from this file. The uploader's choice: the
	// project default, or a per-upload answer. Defaults to 'authorized', the group
	// every record written before the setting existed used. The SAME value must
	// reach the indexing prompt, or the agent's rows land in a different table
	// than the file record and never come back with it.
	accessGroup: UploadAccessGroup = DEFAULT_UPLOAD_ACCESS_GROUP,
): Promise<void> {
	if (!service || !storagePath) return;
	try {
		await (skapi as any).postRecord(null, {
			service,
			unique_id: 'src::' + storagePath,
			table: { name: 'file_summaries', access_group: normalizeUploadAccessGroup(accessGroup) },
			// Deleting this record must take every record extracted from the file with it.
			// Without the flag the backend's cascade is skipped outright: del_records only
			// follows references when the REFERENCED record carries
			// prv_acs.can_remove_referencing_records, which is what `source` populates. So
			// deleting the file left its rows and photos behind. deleteFileIndexRecord's
			// unique_id sweep does not cover them either, because it matches ids starting
			// with "src::<path>" while the agent names row/photo records things like
			// "<file>::<sheet>::PHOTO-C28-1", which share no prefix with it. The cascade is
			// the only mechanism that catches a child whatever unique_id it was given.
			source: { can_remove_referencing_records: true },
			data: {
				file_name: meta?.name || storagePath.split('/').pop() || storagePath,
				storage_path: storagePath,
				mime_type: meta?.mime || null,
				size_bytes: typeof meta?.size === 'number' ? meta.size : null,
				indexed_at: Date.now(),
				note: 'File-level record created at upload. The indexing agent enriches this with sheet names, column headers and row counts.',
			},
		});
	} catch (err) {
		// Non-fatal by design: if this fails, indexing still runs and the agent may create the
		// record itself as it did before. Losing the guarantee is better than losing the upload.
		console.warn('[ai_agent] ensureFileIndexRecord failed (non-fatal)', storagePath, err);
	}
}

// App-level shared state — NOT engine-owned. Populated by dbfile.vue immediately
// when notifyAgentSaveAttachment returns, drained by agent.vue to inject
// "Thinking…" bubbles, and watched by main.vue's fallback poller. Must stay
// Vue-reactive here so those components react to changes.
export const bgTaskQueue: BgTaskEntry[] = reactive([]);

// True while the agent chat view (agent.vue) is mounted. While it's open it owns
// polling/rendering of bgTaskQueue tasks; while closed, main.vue's fallback
// poller drives task completion so records still auto-refresh.
export const agentViewMounted = ref(false);

/* ---- shared ChatSession singleton (Stage E) -------------------------------
 * The chat state machine lives in bunnyquery/engine. agent.vue mounts/unmounts
 * on every service/tab switch, so the session is a module-level SINGLETON (its
 * lifetime = the app session): a per-component session would drop bgTaskQueue /
 * pendingAgentRequests / history-cache state on unmount and break the
 * dbfile.vue → queue → main.vue fallback-poller coordination.
 *
 * The host (identity, DOM, scroll, Service-class I/O) is owned by the VIEW, which
 * installs a concrete ChatHost on mount via setChatHost() and clears it on
 * unmount. The proxy throws only if an engine hook fires with no view mounted
 * (a late poll after unmount — the view guards those paths). */
let _liveHost: ChatHost | null = null;

/**
 * Identity WITHOUT the chat view, for the paths that legitimately run without it.
 *
 * dbfile.vue uploads and indexes files from the db files page, where the chat is not
 * mounted, and ChatSession.claimIndexRun -> hasLiveIndexRun -> _indexClaimKey reads
 * getIdentity() to scope a claim per project and platform (one ChatSession serves every
 * project, so an unscoped claim would collide across them). With the throwing proxy that
 * made every upload from that page fail with "chat host is not mounted", which is a real
 * bug and not a guard doing its job: the claim does not need a view, only a project.
 *
 * Read from `currentService`, the app-level state BOTH views already import, and derive
 * the platform from the project's own ai_agent setting, which is the same value
 * agent.vue's picker is initialised from. So the key computed here and the key computed
 * while the chat is open are the same key, which is the whole point of a claim.
 */
function fallbackIdentity(): ChatIdentity {
	const svc: any = currentService?.value;
	const projectId = svc?.id ?? '';
	return {
		projectId,
		publicProjectId: svc?.service?.projectId || undefined,
		owner: svc?.owner ?? '',
		userId: (skapi as any).user?.user_id || projectId || '',
		platform: (parseAiAgentValue(svc?.service?.ai_agent).platform ?? 'none') as 'claude' | 'openai' | 'none',
		serviceName: svc?.service?.name,
		serviceDescription: svc?.service?.description,
	};
}

const hostProxy = new Proxy({} as ChatHost, {
	get(_t, key) {
		if (!_liveHost) {
			// getIdentity is answerable without a view, and refusing it breaks file
			// indexing from the db files page. Everything else on ChatHost genuinely
			// needs the view (it renders, scrolls, or notifies), so those still throw:
			// a late poll firing after unmount is a bug worth hearing about.
			if (key === 'getIdentity') return fallbackIdentity;
			throw new Error('[ai_agent] chat host is not mounted');
		}
		return (_liveHost as any)[key];
	},
});
export const chatSession = new ChatSession(hostProxy);
export function setChatHost(h: ChatHost | null) { _liveHost = h; }

// Make the engine's plain state POJO Vue-reactive so the view's
// computed(() => chatSession.state.<scalar>) track the engine's in-place writes.
// messages/attachments are additionally driven by shallowRef+triggerRef in the
// view (the engine mutates those arrays in place).
chatSession.state = reactive(chatSession.state);
// Share the bg-indexing queue (also read/written by dbfile.vue + main.vue) with
// the engine instance. aiChatHistoryCache + pendingAgentRequests are re-pointed
// at the project-list shared stores from within agent.vue (which imports them),
// avoiding an ai_agent ↔ project-list circular import here.
chatSession.bgTaskQueue = bgTaskQueue;
